import {
  PDFDocument,
  PDFFont,
  PDFImage,
  PDFPage,
  RGB,
  StandardFonts,
  appendBezierCurve,
  clip,
  closePath,
  endPath,
  moveTo,
  popGraphicsState,
  pushGraphicsState,
  rgb,
} from "pdf-lib";

/** A4 in PDF points (1 pt = 1/72 in). */
export const A4 = { width: 595.28, height: 841.89 } as const;

/** Millimetres to PDF points, for layouts that were authored in mm. */
export const mm = (value: number) => value * 2.834645669;

/** CSS pixels (96 dpi) to PDF points. */
export const px = (value: number) => value * 0.75;

/** Save PDF bytes to the user's machine under `filename`. */
export function downloadPdf(bytes: Uint8Array, filename: string) {
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Standard PDF fonts are WinAnsi-encoded and throw on glyphs outside that
 * range (emoji, CJK, ...). Replace such characters so a receipt never fails
 * to generate because of a brand's description.
 */
export function encodable(font: PDFFont, text: string): string {
  try {
    font.encodeText(text);
    return text;
  } catch {
    return text.replace(/[^\x20-\x7E\xA0-\xFF]/g, "?");
  }
}

type Align = "left" | "center" | "right";

/**
 * Draw a single line of text using a top-left origin (like the browser),
 * converting to pdf-lib's bottom-left coordinate system internally.
 */
export function drawText(
  page: PDFPage,
  font: PDFFont,
  text: string,
  opts: { x: number; y: number; size: number; align?: Align; color?: RGB }
) {
  const safe = encodable(font, text);
  const width = font.widthOfTextAtSize(safe, opts.size);
  const x =
    opts.align === "center"
      ? opts.x - width / 2
      : opts.align === "right"
        ? opts.x - width
        : opts.x;
  // `y` is the text baseline measured from the top of the page.
  const baseline = page.getHeight() - opts.y;
  page.drawText(safe, {
    x,
    y: baseline,
    size: opts.size,
    font,
    color: opts.color ?? rgb(0, 0, 0),
  });
  return width;
}

/**
 * Fetch an image URL (or data URI) and embed it. Returns null when the image
 * cannot be fetched (e.g. CORS) or is neither PNG nor JPEG, so callers can
 * skip the logo rather than fail the whole export.
 */
export async function embedImageFromUrl(
  doc: PDFDocument,
  url: string
): Promise<PDFImage | null> {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const bytes = new Uint8Array(await res.arrayBuffer());
    if (isPng(bytes)) return await doc.embedPng(bytes);
    if (isJpeg(bytes)) return await doc.embedJpg(bytes);
    return null;
  } catch {
    return null;
  }
}

function isPng(b: Uint8Array) {
  return b.length > 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;
}

function isJpeg(b: Uint8Array) {
  return b.length > 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff;
}

export async function createDocument() {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  return { doc, font, bold };
}

/**
 * Draw `image` inside a circle (centre in top-left coordinates), scaled to
 * cover it like CSS `object-fit: cover`; the overflow is clipped away.
 */
export function drawCircularImage(
  page: PDFPage,
  image: PDFImage,
  opts: { cx: number; cy: number; radius: number }
) {
  const { cx, radius: r } = opts;
  const cy = page.getHeight() - opts.cy;
  const k = 0.5523 * r; // bezier handle for a quarter circle
  page.pushOperators(
    pushGraphicsState(),
    moveTo(cx + r, cy),
    appendBezierCurve(cx + r, cy + k, cx + k, cy + r, cx, cy + r),
    appendBezierCurve(cx - k, cy + r, cx - r, cy + k, cx - r, cy),
    appendBezierCurve(cx - r, cy - k, cx - k, cy - r, cx, cy - r),
    appendBezierCurve(cx + k, cy - r, cx + r, cy - k, cx + r, cy),
    closePath(),
    clip(),
    endPath()
  );
  const scale = (2 * r) / Math.min(image.width, image.height);
  const w = image.width * scale;
  const h = image.height * scale;
  page.drawImage(image, { x: cx - w / 2, y: cy - h / 2, width: w, height: h });
  page.pushOperators(popGraphicsState());
}
